import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { segmentApi, TokenStorage } from '@edulastic/api'
import { EduJoyride } from '@edulastic/common'
import {
  EDUCATION_JOYRIDE_SEGMENT_CTA,
  TRACK_IDS,
} from '@edulastic/constants/educationJoyrides'
import { ACTIONS, EVENTS, LIFECYCLE } from 'react-joyride'
import {
  buildTrackStateAfterDismiss,
  resolveJoyrideVariant,
} from './resolveJoyrideVariant'
import {
  adjustEducationJoyrideSpotlight,
  observeEducationJoyrideSpotlight,
  repositionEducationJoyrideTooltip,
} from './adjustJoyrideSpotlight'
import {
  EDUCATION_JOYRIDE_LOCALE,
  EDUCATION_JOYRIDE_SPOTLIGHT_PADDING,
  EDUCATION_JOYRIDE_TARGET_WAIT_MS,
  getEducationJoyrideStyles,
} from './constants'
import { getTrackConfig } from './tracks'
import EducationJoyrideTooltip from './EducationJoyrideTooltip'
import { getModerateTrackContent } from './moderateTrackContent'
import {
  educationJoyridesLoadingSelector,
  educationJoyridesTracksSelector,
  storeEducationJoyridesAction,
} from '../../../../educationJoyrides/educationJoyridesReducer'

const focusJoyrideTarget = (targetSelector) => {
  const targetEl = document.querySelector(targetSelector)
  if (!targetEl) return

  if (targetEl instanceof HTMLElement) {
    if (!targetEl.hasAttribute('tabindex')) {
      targetEl.setAttribute('tabindex', '-1')
    }
    targetEl.focus({ preventScroll: true, focusVisible: false })
  }
}

const EducationJoyride = ({
  trackId,
  trackContext,
  featureGate = true,
  legacyBlocked = false,
  layoutBlocked = false,
  contextsLoading: contextsLoadingProp,
  tracks,
  storeEducationJoyrides,
  joyridesLoading,
  contextsLoadingFromState,
  targetWaitMs = EDUCATION_JOYRIDE_TARGET_WAIT_MS,
  onBeforeStart,
  onRunChange,
  onDismiss,
}) => {
  const contextsLoading = contextsLoadingProp ?? contextsLoadingFromState
  const [run, setRun] = useState(false)
  const skippedPersistRef = useRef(false)
  // One dismissal per run: react-joyride can report the end of a tour more
  // than once (step complete, then tour end).
  const dismissedRef = useRef(false)

  const trackConfig = useMemo(() => getTrackConfig(trackId), [trackId])
  const trackState = tracks?.[trackId]
  // Tracks may supply their own variant rule (e.g. notify-style tracks that
  // re-show per notify); `trackContext` carries the inputs it needs.
  const variant = useMemo(
    () =>
      trackConfig?.resolveVariant
        ? trackConfig.resolveVariant(trackState, trackContext)
        : resolveJoyrideVariant(trackState),
    [trackConfig, trackState, trackContext]
  )

  const shouldEvaluate =
    featureGate &&
    !legacyBlocked &&
    !layoutBlocked &&
    !contextsLoading &&
    trackConfig &&
    // While proxying, never evaluate: prevents the dismiss handler from writing
    // joyride phase state onto the real (proxied) user's account.
    !TokenStorage.isJoyrideDisabledForProxy()

  useEffect(() => {
    if (run) dismissedRef.current = false
    onRunChange?.(run)
  }, [run, onRunChange])

  useEffect(() => {
    if (
      !shouldEvaluate ||
      variant !== 'moderate_skipped' ||
      skippedPersistRef.current
    ) {
      return
    }

    skippedPersistRef.current = true
    const nextTrackState = buildTrackStateAfterDismiss({
      trackState,
      variant: 'moderate_skipped',
    })
    storeEducationJoyrides({
      tracks: {
        ...tracks,
        [trackId]: nextTrackState,
      },
    })
  }, [
    shouldEvaluate,
    variant,
    trackId,
    trackState,
    tracks,
    storeEducationJoyrides,
  ])

  useEffect(() => {
    if (
      !shouldEvaluate ||
      !variant ||
      variant === 'moderate_skipped' ||
      joyridesLoading
    ) {
      setRun(false)
      return undefined
    }

    const { target } = trackConfig
    let cancelled = false
    let hasStarted = false
    let timeoutId
    let observer

    const stopObserving = () => {
      observer?.disconnect()
      clearTimeout(timeoutId)
    }

    const startWhenTargetReady = async () => {
      if (hasStarted || cancelled) return false

      const targetEl = document.querySelector(target)
      if (!targetEl) return false

      if (onBeforeStart) {
        await onBeforeStart()
      }
      if (cancelled) return false

      hasStarted = true
      if (trackConfig.scrollIntoView !== false) {
        targetEl.scrollIntoView({ behavior: 'instant', block: 'center' })
      }
      setRun(true)
      stopObserving()
      return true
    }

    startWhenTargetReady()

    observer = new MutationObserver(() => {
      startWhenTargetReady()
    })
    observer.observe(document.body, { childList: true, subtree: true })

    timeoutId = setTimeout(() => {
      if (!hasStarted) {
        setRun(false)
      }
      stopObserving()
    }, targetWaitMs)

    return () => {
      cancelled = true
      stopObserving()
    }
  }, [
    shouldEvaluate,
    variant,
    trackConfig,
    joyridesLoading,
    targetWaitMs,
    onBeforeStart,
  ])

  // Targets that may still be settling when the tour starts (e.g. a sidebar
  // footer item) can ask for one re-measure: react-floater only re-positions
  // on resize.
  useEffect(() => {
    if (!run || !trackConfig?.repositionAfterStartMs) return undefined
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, trackConfig.repositionAfterStartMs)
    return () => clearTimeout(timer)
  }, [run, trackConfig])

  useEffect(() => {
    if (!run || !trackConfig?.target || variant !== 'aggressive') {
      return undefined
    }

    return observeEducationJoyrideSpotlight(trackConfig.target, {
      padding: EDUCATION_JOYRIDE_SPOTLIGHT_PADDING,
      useOuterTarget: trackConfig.spotlightUsesOuterTarget,
    })
  }, [run, trackConfig, variant])

  const persistDismiss = useCallback(
    (dismissVariant, userResponse) => {
      if (dismissedRef.current) return
      dismissedRef.current = true
      onDismiss?.()
      const nextTrackState = trackConfig?.buildDismissedState
        ? trackConfig.buildDismissedState({
            trackState,
            variant: dismissVariant,
            trackContext,
          })
        : buildTrackStateAfterDismiss({
            trackState,
            variant: dismissVariant,
          })
      storeEducationJoyrides({
        tracks: {
          ...tracks,
          [trackId]: nextTrackState,
        },
      })
      const segmentEvent = trackConfig?.buildSegmentEvent
        ? trackConfig.buildSegmentEvent({
            userResponse,
            variant: dismissVariant,
            trackContext,
          })
        : {
            event: 'Joyride',
            properties: {
              userResponse,
              CTA_Name: EDUCATION_JOYRIDE_SEGMENT_CTA[trackId],
              pagePath: window.location.pathname,
              popup_occurrence: dismissVariant === 'aggressive' ? 1 : 2,
              joyride_track: trackId,
              joyride_variant: dismissVariant,
            },
          }
      segmentApi.genericEventTrack(segmentEvent.event, segmentEvent.properties)
      window.setTimeout(() => {
        setRun(false)
        if (
          trackId !== TRACK_IDS.CURRICULUM_PA &&
          trackConfig?.focusTargetOnDismiss !== false
        ) {
          focusJoyrideTarget(trackConfig?.target)
        }
      }, 0)
    },
    [
      trackId,
      trackState,
      trackContext,
      tracks,
      storeEducationJoyrides,
      onDismiss,
      trackConfig,
    ]
  )

  const handleCallback = useCallback(
    (data) => {
      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        setRun(false)
        return
      }

      if (
        variant === 'aggressive' &&
        (data.type === EVENTS.TOUR_START ||
          data.type === EVENTS.STEP_BEFORE ||
          data.type === EVENTS.STEP_AFTER)
      ) {
        adjustEducationJoyrideSpotlight(trackConfig.target, {
          padding: EDUCATION_JOYRIDE_SPOTLIGHT_PADDING,
          useOuterTarget: trackConfig.spotlightUsesOuterTarget,
        })
        if (data.type === EVENTS.TOUR_START) {
          window.requestAnimationFrame(() => {
            repositionEducationJoyrideTooltip()
          })
        }
      }

      if (data.lifecycle !== LIFECYCLE.COMPLETE) return

      const { CLOSE, NEXT, LAST } = ACTIONS
      const isCtaComplete = data.action === NEXT || data.action === LAST
      const isClose = data.action === CLOSE

      if (variant === 'aggressive') {
        if (!isCtaComplete && !isClose) return
        persistDismiss('aggressive', isClose ? 'Close' : 'clickCTA')
        return
      }

      if (variant === 'moderate') {
        if (!isClose && !isCtaComplete) return
        persistDismiss('moderate', isClose ? 'Close' : 'clickCTA')
      }
    },
    [variant, persistDismiss, trackConfig]
  )

  const descId = `education-joyride-desc-${trackId}`
  const moderateContent = useMemo(() => getModerateTrackContent(trackId), [
    trackId,
  ])
  // A moderate step without a title renders no heading, so label the dialog
  // by its message instead.
  const titleId =
    variant === 'moderate' && moderateContent && !moderateContent.title
      ? descId
      : `education-joyride-title-${trackId}`

  const renderTooltip = useCallback(
    (tooltipProps) => (
      <EducationJoyrideTooltip
        {...tooltipProps}
        variant={variant}
        moderateIcon={moderateContent?.Icon}
        titleId={titleId}
        descId={descId}
        onDismissInteraction={onDismiss}
      />
    ),
    [variant, moderateContent, titleId, descId, onDismiss]
  )

  if (
    !shouldEvaluate ||
    !variant ||
    variant === 'moderate_skipped' ||
    !trackConfig
  ) {
    return null
  }

  const steps = [
    trackConfig.buildStep({
      target: trackConfig.target,
      variant,
      titleId,
      descId,
    }),
  ]

  return (
    <EduJoyride
      variant={variant}
      hideCloseButton
      run={run}
      steps={steps}
      callback={handleCallback}
      // Single-step tours are non-continuous, so react-joyride reports the CTA
      // as a close. Tracks that must tell the CTA from the X opt in.
      continuous={!!trackConfig.continuous}
      locale={trackConfig.locale ?? EDUCATION_JOYRIDE_LOCALE}
      styles={getEducationJoyrideStyles(variant)}
      spotlightPadding={
        variant === 'aggressive' ? EDUCATION_JOYRIDE_SPOTLIGHT_PADDING : 0
      }
      spotlightClicks={false}
      tooltipComponent={renderTooltip}
      disableScrolling={trackConfig.scrollIntoView === false}
      options={{ zIndex: 1015 }}
    />
  )
}

export default connect(
  (state) => ({
    tracks: educationJoyridesTracksSelector(state),
    joyridesLoading: educationJoyridesLoadingSelector(state),
    contextsLoadingFromState:
      state.dashboardTeacher?.userContextLoading ?? false,
  }),
  {
    storeEducationJoyrides: storeEducationJoyridesAction,
  }
)(EducationJoyride)
